/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * PickerDebugPanel — operator-visible iPhone picker handoff
 * diagnostic strip.
 *
 * P0 (May 20, 2026 — production retest failed):
 *   Console logs are not enough — the operator is testing on a
 *   real iPhone and cannot reach DevTools. This panel surfaces
 *   the entire picker lifecycle ON-DEVICE so the failure step
 *   is visible from the screen alone.
 *
 * Enable on iPhone production by appending `?debugPicker=1` to
 * any URL once (the flag persists in localStorage):
 *   https://nationaldrivingguild.co.uk/lesson-review?debugPicker=1
 *
 * To disable: `?debugPicker=0`.
 *
 * The panel renders top-right, floats above all surfaces with a
 * z-index high enough to survive modal stacks. Tap the header to
 * collapse / expand. Tap the trash icon to clear the event log.
 *
 * Shows:
 *   • PICKER_FIX_VERSION — proves the deployed bundle has the fix.
 *   • Counter chips for each event type — at-a-glance state.
 *   • Live last-30 event tail with relative timestamps.
 *
 * NOT MOUNTED for normal users. Only mounted when the flag is on.
 */
import { useEffect, useState } from "react";
import {
  PICKER_FIX_VERSION,
  subscribePickerEvents,
  isPickerDebugEnabled,
  syncPickerDebugFlagFromUrl,
} from "../lib/pickerDebug";

const TYPE_COLOR = {
  handoff_attempt:         "bg-indigo-500/20 text-indigo-200 border-indigo-500/40",
  handoff_beacon_written:  "bg-sky-500/20 text-sky-200 border-sky-500/40",
  handoff_onchange:        "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  handoff_empty:           "bg-amber-500/20 text-amber-200 border-amber-500/40",
  handoff_files_staged:    "bg-emerald-600/30 text-emerald-100 border-emerald-500/50",
  handoff_missed:          "bg-rose-600/30 text-rose-100 border-rose-500/50",
  handoff_recovered:       "bg-emerald-700/30 text-emerald-100 border-emerald-500/60",
  modal_mount:             "bg-slate-700/40 text-slate-200 border-slate-500/40",
  modal_unmount:           "bg-slate-800/60 text-slate-300 border-slate-600/60",
  eager_upload_started:    "bg-fuchsia-500/25 text-fuchsia-100 border-fuchsia-400/50",
  eager_upload_init_ok:    "bg-emerald-700/40 text-emerald-50 border-emerald-400/60",
  eager_upload_init_failed:"bg-rose-700/40 text-rose-50 border-rose-400/60",
  v2_picker_returned:           "bg-cyan-500/20 text-cyan-100 border-cyan-400/40",
  v2_local_rows_created:        "bg-cyan-600/30 text-cyan-50 border-cyan-400/50",
  v2_draft_saved:               "bg-cyan-700/30 text-cyan-50 border-cyan-400/50",
  v2_backend_session_created:   "bg-teal-700/30 text-teal-50 border-teal-400/50",
  v2_upload_started:            "bg-fuchsia-700/30 text-fuchsia-50 border-fuchsia-400/50",
  v2_upload_completed:          "bg-emerald-800/40 text-emerald-50 border-emerald-400/60",
  v2_review_created:            "bg-emerald-900/50 text-emerald-50 border-emerald-300/60",
  v2_hydration_blocked_or_merged:"bg-amber-700/30 text-amber-50 border-amber-400/50",
  v2_remount:                   "bg-slate-600/40 text-slate-100 border-slate-400/50",
};

const TYPE_LABEL = {
  handoff_attempt:         "→ Photos",
  handoff_beacon_written:  "Beacon",
  handoff_onchange:        "onChange",
  handoff_empty:           "0 files",
  handoff_files_staged:    "Staged",
  handoff_missed:          "MISSED",
  handoff_recovered:       "Recovered",
  modal_mount:             "+Modal",
  modal_unmount:           "-Modal",
  eager_upload_started:    "EAGER",
  eager_upload_init_ok:    "init OK",
  eager_upload_init_failed:"init FAIL",
  v2_picker_returned:           "v2 picker",
  v2_local_rows_created:        "v2 rows",
  v2_draft_saved:               "v2 draft",
  v2_backend_session_created:   "v2 session",
  v2_upload_started:            "v2 up start",
  v2_upload_completed:          "v2 up done",
  v2_review_created:            "v2 review",
  v2_hydration_blocked_or_merged:"v2 hydr",
  v2_remount:                   "v2 remount",
};

function relativeSince(at) {
  const ms = Date.now() - at;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m`;
}

export default function PickerDebugPanel() {
  const [enabled, setEnabled] = useState(false);
  const [events, setEvents] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  // Force re-render every second so the relative timestamps tick.
  const [, setTick] = useState(0);

  useEffect(() => {
    syncPickerDebugFlagFromUrl();
    setEnabled(isPickerDebugEnabled());
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const off = subscribePickerEvents((_evt, ring) => {
      setEvents(ring.slice(-30).reverse());
    });
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => {
      off();
      clearInterval(id);
    };
  }, [enabled]);

  if (!enabled) return null;

  // Counters by type — operator can see at a glance "did onChange
  // fire?", "did the beacon get written?", etc.
  const counts = events.reduce((acc, e) => {
    acc[e.type] = (acc[e.type] || 0) + 1;
    return acc;
  }, {});

  // Last MISSED event surfaces with extra prominence — that is the
  // failure the operator is trying to diagnose.
  const lastMissed = events.find((e) => e.type === "handoff_missed");

  return (
    <div
      data-testid="picker-debug-panel"
      className="mx-auto mt-3 max-w-md w-full text-[10px] font-mono select-none pointer-events-auto"
      style={{ fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace" }}
    >
      <div className="rounded-lg border border-slate-700 bg-slate-950/95 backdrop-blur-sm shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)] text-slate-200">
        <button
          type="button"
          data-testid="picker-debug-panel-toggle"
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-between px-2.5 py-1.5 border-b border-slate-800 text-left"
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-extrabold text-amber-300">PICKER</span>
            <span
              data-testid="picker-debug-build-stamp"
              className="text-slate-400 truncate"
            >
              {PICKER_FIX_VERSION}
            </span>
          </span>
          <span className="text-slate-500">
            {collapsed ? "▸" : "▾"}
          </span>
        </button>

        {!collapsed && (
          <div className="px-2.5 py-2 space-y-2 max-h-[60vh] overflow-y-auto overscroll-contain">
            {/* Counter chip strip */}
            <div className="flex flex-wrap gap-1">
              {[
                "handoff_attempt",
                "handoff_beacon_written",
                "handoff_onchange",
                "v2_picker_returned",
                "v2_local_rows_created",
                "v2_draft_saved",
                "v2_backend_session_created",
                "v2_upload_started",
                "v2_upload_completed",
                "v2_review_created",
                "v2_hydration_blocked_or_merged",
                "handoff_missed",
              ].map((t) => (
                <span
                  key={t}
                  data-testid={`picker-debug-count-${t}`}
                  className={`px-1.5 py-0.5 rounded border ${TYPE_COLOR[t]} flex items-center gap-1`}
                >
                  <span className="font-extrabold">{TYPE_LABEL[t]}</span>
                  <span className="opacity-80">{counts[t] || 0}</span>
                </span>
              ))}
            </div>

            {lastMissed && (
              <div
                data-testid="picker-debug-missed-alert"
                className="rounded border border-rose-500/50 bg-rose-500/10 px-2 py-1.5 text-rose-100"
              >
                <p className="font-extrabold text-[10.5px]">⚠ Handoff missed</p>
                <p className="text-[9.5px] opacity-90 leading-tight mt-0.5">
                  Photos handoff fired but onChange never returned.
                  Source: {lastMissed.payload?.source || "?"} ·{" "}
                  {relativeSince(lastMissed.at)} ago.
                </p>
              </div>
            )}

            {events.length === 0 ? (
              <p className="text-slate-500 italic text-[10px]">
                No picker events yet. Tap your upload button to begin.
              </p>
            ) : (
              <ul className="space-y-1 text-[9.5px] leading-tight">
                {events.map((e, i) => (
                  <li
                    key={`${e.at}-${i}`}
                    data-testid={`picker-debug-event-${e.type}`}
                    className="flex items-start gap-1.5"
                  >
                    <span
                      className={`shrink-0 px-1 py-[1px] rounded border ${TYPE_COLOR[e.type]} font-extrabold`}
                    >
                      {TYPE_LABEL[e.type]}
                    </span>
                    <span className="text-slate-500 shrink-0">
                      {relativeSince(e.at)}
                    </span>
                    <span className="text-slate-300 break-all min-w-0">
                      {summarisePayload(e.payload)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function summarisePayload(p) {
  if (!p || typeof p !== "object") return "";
  // Keep the most operator-useful fields, short.
  const keys = [
    "source",
    "file_count",
    "first_size",
    "first_type",
    "basket_id",
    "route",
    "trigger",
  ];
  const parts = [];
  for (const k of keys) {
    if (p[k] != null) {
      let v = p[k];
      if (typeof v === "string" && v.length > 30) v = `${v.slice(0, 28)}…`;
      parts.push(`${k}=${v}`);
    }
  }
  return parts.join(" ") || "(no payload)";
}
